import React, { useEffect, useMemo, useState } from 'react';
import { ProjectList } from './components/ProjectList';
import { CreateProject } from './components/CreateProject';
import { MainProject } from './components/MainProject';
import { Auth } from './components/Auth';
import { useProjects } from './hooks/useProjects';
import { useAuth } from './contexts/AuthContext';
import { Project } from './types';

type AppState = 
  | { type: 'list' }
  | { type: 'create' }
  | { type: 'project'; project: Project };

function App() {
  const { user, loading: authLoading } = useAuth();
  const { projects, assignedProjects, loading: projectsLoading, addProject } = useProjects();
  const [appState, setAppState] = useState<AppState>({ type: 'list' });
  const allProjects = useMemo(() => [...projects, ...assignedProjects], [projects, assignedProjects]);
  const [pendingProjectId, setPendingProjectId] = useState<string | null>(null);

  const parseRoute = (): AppState => {
    const { pathname } = window.location;
    const segs = pathname.replace(/\/+$/, '').split('/').filter(Boolean);
    // Support root redirect to /desk
    if (segs.length === 0) {
      window.history.replaceState({}, '', '/desk');
      return { type: 'list' };
    }
    // /desk → assigned list
    if (segs.length === 1 && segs[0] === 'desk') {
      return { type: 'list' };
    }
    // /projects → my projects list
    if (segs.length === 1 && segs[0] === 'projects') {
      return { type: 'list' };
    }
    // /projects/new
    if (segs.length === 2 && segs[0] === 'projects' && segs[1] === 'new') {
      return { type: 'create' };
    }
    // /desk/:id or /projects/:id
    if (segs.length === 2 && (segs[0] === 'desk' || segs[0] === 'projects')) {
      const id = segs[1];
      const p = allProjects.find(pr => pr.id === id);
      if (p) return { type: 'project', project: p };
      setPendingProjectId(id);
      return { type: 'list' };
    }
    return { type: 'list' };
  };

  // Sync app state with current URL path
  useEffect(() => {
    setAppState(parseRoute());
    const onPop = () => setAppState(parseRoute());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If a /projects/:id route was parsed before projects loaded, resolve it when projects arrive
  useEffect(() => {
    if (pendingProjectId) {
      const p = allProjects.find(pr => pr.id === pendingProjectId);
      if (p) {
        setAppState({ type: 'project', project: p });
        setPendingProjectId(null);
      }
    }
  }, [pendingProjectId, allProjects]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }}></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Auth onSuccess={() => window.location.reload()} />;
  }

  if (projectsLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }}></div>
          <p className="text-slate-600">Loading projects...</p>
        </div>
      </div>
    );
  }

  // Do not auto-route to create for taskers; stay on the list view by default

  const handleCreateProject = async (projectData: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const newProject = await addProject(projectData);
      window.history.pushState({}, '', `/projects/${newProject.id}`);
      setAppState({ type: 'project', project: newProject });
    } catch (error) {
      console.error('Failed to create project:', error);
      alert(error instanceof Error ? error.message : 'Failed to create project');
    }
  };

  const handleSelectProject = (project: Project) => {
    const fromDeskList = window.location.pathname.startsWith('/desk');
    const target = fromDeskList ? `/desk/${project.id}` : `/projects/${project.id}`;
    window.history.pushState({}, '', target);
    setAppState({ type: 'project', project });
  };

  const handleBackToList = () => {
    const isDesk = window.location.pathname.startsWith('/desk');
    window.history.pushState({}, '', isDesk ? '/desk' : '/projects');
    setAppState({ type: 'list' });
  };

  const handleCreateNew = () => {
    window.history.pushState({}, '', '/projects/new');
    setAppState({ type: 'create' });
  };

  const activeTabFromPath: 'assigned' | 'mine' = (() => {
    const segs = window.location.pathname.replace(/\/+$/, '').split('/').filter(Boolean);
    return segs[0] === 'projects' ? 'mine' : 'assigned';
  })();

  const handleTabChange = (tab: 'assigned' | 'mine') => {
    if (tab === 'assigned') {
      window.history.pushState({}, '', '/desk');
    } else {
      window.history.pushState({}, '', '/projects');
    }
    setAppState({ type: 'list' });
  };

  switch (appState.type) {
    case 'list':
      return (
        <ProjectList
          projects={projects}
          onSelectProject={handleSelectProject}
          onCreateNew={handleCreateNew}
          activeTab={activeTabFromPath}
          onTabChange={handleTabChange}
        />
      );
    
    case 'create':
      return (
        <CreateProject
          onCreateProject={handleCreateProject}
          onBack={handleBackToList}
        />
      );
    
    case 'project':
      return (
        <MainProject
          project={appState.project}
          onBack={handleBackToList}
        />
      );
    
    default:
      return null;
  }
}

export default App;